import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import HeaderV2 from '../components/HeaderV2'
import ProductHeroV2 from './product/v2/ProductHeroV2'
import ProductInfoV2 from './product/v2/ProductInfoV2'
import ProductSubscriptionV2 from './product/v2/ProductSubscriptionV2'
import ProductDescriptionV2 from './product/v2/ProductDescriptionV2'
import ProductSpecsAccordionV2 from './product/v2/ProductSpecsAccordionV2'
import RelatedProductsCarouselV2 from './product/v2/RelatedProductsCarouselV2'
import { lumi } from '../lib/lumi'
import type { ProductV2Detail, Lang } from './product/v2/types'
import type { ProductV2 } from './shop/v2/types'

/**
 * ProductPageV2 — /{lang}/shop/:category/v2/:id/:slug?
 *
 * Barista Map–styled product detail page. Coexists with the original
 * ProductPage at /{lang}/shop/:category/:id/:slug; the old route preserves
 * existing SEO and structured data, while V2 is the new visual experience.
 *
 * Layout (top-down):
 *   1. HeaderV2 (solid white)
 *   2. Spacer below navbar
 *   3. 50/50 split — ProductHeroV2 (image) | ProductInfoV2 + ProductSubscriptionV2
 *   4. ProductDescriptionV2
 *   5. ProductSpecsAccordionV2 (3 groups, all collapsed)
 *   6. RelatedProductsCarouselV2
 *   7. FooterV2 — injected by the route in App.tsx
 *
 * SEO: This V2 preview uses noindex by default to keep the original
 * /{lang}/shop/:category/:id/:slug as the canonical SEO entry. Once V2
 * becomes the primary product page, swap to robots="index, follow" and
 * inject Product Schema.org here.
 */
const ProductPageV2: React.FC = () => {
  const { category, id } = useParams<{ category?: string; id?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const currentLang: Lang = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const isZh = currentLang === 'zh'
  const isPreviewRoute = location.pathname.includes('/v2')

  const [product, setProduct] = useState<ProductV2Detail | null>(null)
  const [loading, setLoading] = useState(true)
  const [relatedProducts, setRelatedProducts] = useState<ProductV2[]>([])
  const [quantity, setQuantity] = useState(1)
  const [isWishlisted, setIsWishlisted] = useState(false)
  const [favoriteLoading, setFavoriteLoading] = useState(false)
  const [buyMode, setBuyMode] = useState<'single' | 'subscription'>('single')
  const [subscriptionMonths, setSubscriptionMonths] = useState<number>(20)
  const [selectedDeliveryDay, setSelectedDeliveryDay] = useState<number>(10)

  // Inject Barista Map serif fonts once on mount
  useEffect(() => {
    const id = 'productpage-v2-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Noto+Serif+TC:wght@300;400;500&family=Zen+Kaku+Gothic+New:wght@400;500&display=swap'
    document.head.appendChild(link)
  }, [])

  // Fetch product
  useEffect(() => {
    if (!id) return
    let mounted = true
    const run = async () => {
      setLoading(true)
      try {
        const isObjectId = /^[0-9a-fA-F]{24}$/.test(id)
        const numericId = parseInt(id, 10)
        let result: any = null

        if (!isObjectId && !isNaN(numericId) && numericId >= 1001) {
          const response = await lumi.entities.products.list({
            filter: { productId: numericId },
            limit: 1,
          })
          result = response.list?.[0] || null
        }
        if (!result) {
          try {
            result = await lumi.entities.products.get(id)
          } catch (e) {
            console.warn('ProductPageV2: fallback get by _id failed', e)
          }
        }

        if (mounted) setProduct(result || null)
      } catch (e) {
        console.error('ProductPageV2: fetch product failed', e)
        if (mounted) setProduct(null)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => {
      mounted = false
    }
  }, [id])

  // Fetch related (same category, exclude current)
  useEffect(() => {
    if (!product) return
    let mounted = true
    const run = async () => {
      try {
        const { list } = await lumi.entities.products.list({
          filter: { category: product.category, _id: { $ne: product._id } },
          limit: 8,
        })
        if (mounted) setRelatedProducts((list as ProductV2[]) || [])
      } catch (e) {
        console.error('ProductPageV2: fetch related failed', e)
      }
    }
    run()
    return () => {
      mounted = false
    }
  }, [product])

  // Wishlist state
  useEffect(() => {
    if (!product) return
    let mounted = true
    const run = async () => {
      try {
        const user = lumi.auth.user
        if (!user) {
          if (mounted) setIsWishlisted(false)
          return
        }
        const { list } = await lumi.entities.favorites.list({
          filter: { userEmail: user.email, productId: product._id },
          limit: 1,
        })
        if (mounted) setIsWishlisted(list.length > 0)
      } catch (e) {
        console.error('ProductPageV2: check favorite failed', e)
      }
    }
    run()
    return () => {
      mounted = false
    }
  }, [product])

  const handleToggleWishlist = async () => {
    if (!product) return
    const user = lumi.auth.user
    if (!user) {
      alert(isZh ? '請先登入' : 'Please login first')
      return
    }
    setFavoriteLoading(true)
    try {
      if (isWishlisted) {
        const { list } = await lumi.entities.favorites.list({
          filter: { userEmail: user.email, productId: product._id },
        })
        if (list.length > 0) await lumi.entities.favorites.delete(list[0]._id)
        setIsWishlisted(false)
      } else {
        await lumi.entities.favorites.create({
          userEmail: user.email,
          productId: product._id,
          productName: product.name,
          productImage: product.images[0] || '',
          productPrice: product.price,
          createdAt: new Date().toISOString(),
        })
        setIsWishlisted(true)
      }
      window.dispatchEvent(new Event('favoriteChanged'))
    } catch (e) {
      console.error('ProductPageV2: toggle favorite failed', e)
      alert(isZh ? '操作失敗，請稍後再試' : 'Operation failed, please try again')
    } finally {
      setFavoriteLoading(false)
    }
  }

  const handleShare = async () => {
    if (!product) return
    const data = {
      title: isZh ? product.name : (product.nameEn || product.name),
      text: isZh ? product.description : (product.descriptionEn || product.description),
      url: window.location.href,
    }
    try {
      if (navigator.share) {
        await navigator.share(data)
      } else {
        await navigator.clipboard.writeText(window.location.href)
        alert(isZh ? '連結已複製到剪貼板' : 'Link copied to clipboard')
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        console.error('share failed', e)
      }
    }
  }

  const seoTitle = useMemo(() => {
    if (!product) return 'ONGU Studio'
    const name = isZh ? product.name : (product.nameEn || product.name)
    return isZh
      ? `${name}（V2 預覽）｜ONGU Studio Coffee Roasters`
      : `${name} (V2 Preview) | ONGU Studio Coffee Roasters`
  }, [product, isZh])

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F1]">
        <HeaderV2 variant="solid" />
        <div className="flex min-h-[60vh] items-center justify-center pt-20">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-black/20 border-t-black/70" />
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FAF9F1]">
        <HeaderV2 variant="solid" />
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 pt-20 text-center">
          <p className="text-lg text-black/70" style={{ fontFamily: "'EB Garamond', serif" }}>
            {isZh ? '找不到商品' : 'Product not found'}
          </p>
          <button
            onClick={() => navigate(`/${currentLang}/shop/${category || 'beans'}/v2`)}
            className="border border-black px-6 py-3 text-[12px] uppercase text-black hover:bg-black hover:text-white transition-colors"
            style={{ letterSpacing: '0.25em', fontFamily: "'EB Garamond', serif" }}
          >
            {isZh ? '返回商店' : 'Back to shop'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAF9F1]">
      <Helmet>
        <title>{seoTitle}</title>
        {/* /v2 預覽路由維持 noindex；換骨到舊 URL 後讓 useDynamicSEO（從 page_seo / product 設定）接管 */}
        {isPreviewRoute && <meta name="robots" content="noindex, nofollow" />}
        <link
          rel="canonical"
          href={`https://www.ongucoffee.com/${currentLang}/shop/${product.category}/${product.productId || product._id}/${product.slug || ''}`}
        />
      </Helmet>

      <HeaderV2 variant="solid" />

      {/* Spacer below fixed navbar */}
      <div className="h-20" />

      {/* Hero / Info — 50/50 split */}
      <section className="border-t border-black/10">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 lg:grid-cols-2">
          {/* Left: image */}
          <div className="lg:sticky lg:top-20 lg:self-start">
            <ProductHeroV2 product={product} currentLang={currentLang} />
          </div>

          {/* Right: info */}
          <div className="px-6 py-10 md:px-12 md:py-16 lg:px-16 lg:py-20">
            <div className="max-w-[520px]">
              <ProductInfoV2
                product={product}
                currentLang={currentLang}
                quantity={quantity}
                onQuantityChange={setQuantity}
                isWishlisted={isWishlisted}
                favoriteLoading={favoriteLoading}
                onToggleWishlist={handleToggleWishlist}
                onShare={handleShare}
              />

              {product.isSubscription && (
                <div className="mt-8">
                  <ProductSubscriptionV2
                    product={product}
                    currentLang={currentLang}
                    buyMode={buyMode}
                    onModeChange={setBuyMode}
                    subscriptionMonths={subscriptionMonths}
                    onMonthsChange={setSubscriptionMonths}
                    selectedDeliveryDay={selectedDeliveryDay}
                    onDayChange={setSelectedDeliveryDay}
                  />
                </div>
              )}

              {/* Description (moved into right column) */}
              <ProductDescriptionV2 product={product} currentLang={currentLang} />

              {/* Specs / Brewing / Shipping accordion (moved into right column) */}
              <ProductSpecsAccordionV2 product={product} currentLang={currentLang} />
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <RelatedProductsCarouselV2 products={relatedProducts} currentLang={currentLang} />
    </div>
  )
}

export default ProductPageV2
